/*
 * Purpose: Classify flows for dashboard survey — running, ready, building
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import type { Workflow } from '@/stores/workflowStore';
import { WorkflowValidator } from '@/services/validation';
import { missionGoalBlocks, missionStartBlockedReason } from '@/lib/missionRules';

export type FlowLifecycleStatus = 'running' | 'ready' | 'building';

export interface FlowSurveyItem {
  workflow: Workflow;
  status: FlowLifecycleStatus;
  errorCount: number;
  warningCount: number;
  nodeCount: number;
  linkCount: number;
  hasGoal: boolean;
  updatedAt: Date;
}

export interface FlowSurveySummary {
  total: number;
  running: number;
  ready: number;
  building: number;
  activeRecently: number;
  totalNodes: number;
  totalLinks: number;
  items: FlowSurveyItem[];
  runningItems: FlowSurveyItem[];
  readyItems: FlowSurveyItem[];
  buildingItems: FlowSurveyItem[];
}

const ACTIVE_MS = 7 * 24 * 60 * 60 * 1000;

function classifyWorkflow(workflow: Workflow, runningIds: Set<string>): Omit<FlowSurveyItem, 'workflow'> {
  const validation = WorkflowValidator.validate(workflow);
  const errorCount = validation.filter((v) => v.type === 'error').length;
  const warningCount = validation.filter((v) => v.type === 'warning').length;
  const nodeCount = workflow.nodes.length;
  const linkCount = workflow.edges.length;
  const hasGoal = missionGoalBlocks(workflow).length > 0;
  const updatedAt = new Date(workflow.updatedAt);

  if (runningIds.has(workflow.id)) {
    return {
      status: 'running',
      errorCount,
      warningCount,
      nodeCount,
      linkCount,
      hasGoal,
      updatedAt,
    };
  }

  const startBlocked = missionStartBlockedReason(workflow);
  const isMinimal = nodeCount <= 1 || linkCount === 0;
  const incomplete = errorCount > 0 || Boolean(startBlocked) || !hasGoal || isMinimal;

  return {
    status: incomplete ? 'building' : 'ready',
    errorCount,
    warningCount,
    nodeCount,
    linkCount,
    hasGoal,
    updatedAt,
  };
}

export function surveyFlows(workflows: Workflow[], runningWorkflowIds: string[] = []): FlowSurveySummary {
  const runningSet = new Set(runningWorkflowIds);
  const now = Date.now();

  const items: FlowSurveyItem[] = workflows.map((workflow) => ({
    workflow,
    ...classifyWorkflow(workflow, runningSet),
  }));

  items.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

  const runningItems = items.filter((i) => i.status === 'running');
  const readyItems = items.filter((i) => i.status === 'ready');
  const buildingItems = items.filter((i) => i.status === 'building');
  const activeRecently = items.filter((i) => now - i.updatedAt.getTime() <= ACTIVE_MS).length;

  return {
    total: items.length,
    running: runningItems.length,
    ready: readyItems.length,
    building: buildingItems.length,
    activeRecently,
    totalNodes: items.reduce((n, i) => n + i.nodeCount, 0),
    totalLinks: items.reduce((n, i) => n + i.linkCount, 0),
    items,
    runningItems,
    readyItems,
    buildingItems,
  };
}

export function isComposeSessionActive(phase: string, outcomeText: string): boolean {
  if (!outcomeText.trim()) return false;
  return phase !== 'describe' && phase !== 'approved';
}
